import { InputAdornment, TextField, styled } from '@mui/material'
import { tokens } from 'ComponentLibrary/style-dictionary/output'
import { ChangeEventHandler, CSSProperties, FocusEventHandler, forwardRef, KeyboardEventHandler, ReactNode, useId } from 'react'
import { Control, FieldPathByValue, FieldValues, RegisterOptions, UseControllerProps, useController } from 'react-hook-form'

type TextInputType = 'email' | 'password' | 'search' | 'tel' | 'text' | 'url'

export type TextInputProps = {
    'aria-label'?: string
    'aria-labelledby'?: string
    autoFocus?: boolean
    className?: string
    defaultValue?: string
    disabled?: boolean
    errorMessage?: string
    endAdornment?: ReactNode
    inputMode?: 'decimal' | 'numeric'
    label?: string
    name?: string
    multiline?: boolean
    minRows?: number
    maxRows?: number | string
    resize?: CSSProperties['resize']
    onBlur?: FocusEventHandler<HTMLInputElement>
    onChange?: ChangeEventHandler<HTMLInputElement>
    onFocus?: FocusEventHandler<HTMLInputElement>
    onKeyDown?: KeyboardEventHandler<HTMLInputElement>
    placeholder?: string
    required?: boolean
    textAlign?: 'left' | 'right'
    type?: TextInputType
    unit?: string
    value?: string
}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput(
    {
        'aria-label': ariaLabel,
        'aria-labelledby': ariaLabelledBy,
        autoFocus,
        className,
        defaultValue,
        disabled,
        errorMessage,
        endAdornment,
        inputMode,
        label,
        name,
        multiline,
        minRows = 2,
        maxRows,
        resize = 'vertical',
        onBlur,
        onChange,
        onFocus,
        onKeyDown,
        placeholder,
        required,
        textAlign = 'left',
        type = 'text',
        unit,
        value,
    },
    ref
) {
    const inputId = useId()

    return (
        <StyledTextField
            $hasUnit={!!unit || !!endAdornment}
            $textAlign={textAlign}
            autoFocus={autoFocus}
            className={className}
            defaultValue={defaultValue}
            disabled={disabled}
            error={errorMessage !== undefined}
            helperText={errorMessage}
            id={inputId}
            inputRef={ref}
            label={label}
            name={name}
            multiline={multiline}
            minRows={multiline ? minRows : undefined}
            maxRows={multiline ? maxRows : undefined}
            onBlur={onBlur}
            onChange={onChange}
            onFocus={onFocus}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            required={required}
            slotProps={{
                htmlInput: {
                    'aria-label': ariaLabel,
                    'aria-labelledby': ariaLabelledBy,
                    inputMode,
                    style: multiline ? { resize } : undefined,
                },
                input: {
                    endAdornment:
                        unit || endAdornment ? (
                            <InputAdornment disableTypography position="end">
                                {unit}
                                {endAdornment}
                            </InputAdornment>
                        ) : undefined,
                },
            }}
            type={type}
            value={value}
            variant="outlined"
        />
    )
})

type StringFieldPath<TFieldValues extends FieldValues> = FieldPathByValue<TFieldValues, string | undefined>

type FormTextInputRules<TFieldValues extends FieldValues, TName extends StringFieldPath<TFieldValues>> = NonNullable<
    UseControllerProps<TFieldValues, TName>['rules']
>

export type FormTextInputProps<
    TFieldValues extends FieldValues,
    TName extends StringFieldPath<TFieldValues> = StringFieldPath<TFieldValues>,
> = Omit<TextInputProps, 'defaultValue' | 'errorMessage' | 'name' | 'required' | 'value'> & {
    control: Control<TFieldValues>
    defaultValue?: UseControllerProps<TFieldValues, TName>['defaultValue']
    name: TName
    rules?: FormTextInputRules<TFieldValues, TName>
}

export function FormTextInput<
    TFieldValues extends FieldValues,
    TName extends StringFieldPath<TFieldValues> = StringFieldPath<TFieldValues>,
>({ control, defaultValue, name, onBlur, onChange, rules, ...textInputProps }: FormTextInputProps<TFieldValues, TName>) {
    const { field, fieldState } = useController({
        control,
        defaultValue,
        name,
        rules,
    })

    return (
        <TextInput
            {...textInputProps}
            errorMessage={fieldState.error ? (fieldState.error.message?.toString() ?? '') : undefined}
            name={field.name}
            onBlur={(event) => {
                field.onBlur()
                onBlur?.(event)
            }}
            onChange={(event) => {
                field.onChange(event.target.value)
                onChange?.(event)
            }}
            ref={field.ref}
            required={isRequiredRule(rules?.required)}
            value={field.value ?? ''}
        />
    )
}

export function isRequiredRule(required: RegisterOptions['required']) {
    return typeof required === 'object' ? required.value : Boolean(required)
}

const StyledTextField = styled(TextField, {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $hasUnit: boolean; $textAlign: 'left' | 'right' }>(({ $hasUnit, $textAlign }) => ({
    minHeight: '46px',
    minWidth: 0,
    position: 'relative',
    width: '100%',
    '& .MuiInputLabel-root': {
        ...tokens.TextS,
        color: tokens.ColorTextSubtle,
        maxWidth: $hasUnit ? 'calc(100% - 64px)' : 'calc(100% - 24px)',
        transform: `translate(${tokens.SizingSpacing150}, 14px) scale(1)`,
        zIndex: 1,
        '&.Mui-focused': {
            color: tokens.ColorTextSubtle,
        },
        '&.Mui-error': {
            color: tokens.ColorTextDangerSubtle,
        },
        '&.Mui-disabled': {
            color: tokens.ColorTextDisabled,
        },
        '&.MuiInputLabel-shrink': {
            ...tokens.TextXs,
            backgroundColor: tokens.ColorSurfaceDefault,
            maxWidth: 'calc(100% - 24px)',
            padding: `0 ${tokens.SizingSpacing075}`,
            transform: `translate(${tokens.SizingSpacing075}, -7px) scale(1)`,
        },
        '&.MuiInputLabel-shrink.Mui-disabled': {
            backgroundColor: 'transparent',
        },
    },
    '&:has(.MuiOutlinedInput-root:hover, .MuiInputLabel-shrink:hover)': {
        '& .MuiInputLabel-shrink:not(.Mui-focused):not(.Mui-disabled)': {
            backgroundColor: 'transparent',
        },
        '& .MuiOutlinedInput-root:not(.Mui-focused):not(.Mui-disabled)': {
            backgroundColor: tokens.ColorSurfaceHover,
        },
        '& .MuiOutlinedInput-root:not(.Mui-focused):not(.Mui-disabled):not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.ColorBorderDefault,
        },
    },
    '& .MuiOutlinedInput-root': {
        ...tokens.TextS,
        backgroundColor: tokens.ColorSurfaceDefault,
        borderRadius: tokens.SizingCornerS,
        color: tokens.ColorTextBold,
        gap: tokens.SizingSpacing100,
        height: '46px',
        padding: `0 ${tokens.SizingSpacing150}`,
        '&.MuiInputBase-multiline': {
            height: 'auto',
            padding: tokens.SizingSpacing150,
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.ColorBorderDefault,
            borderWidth: tokens.SizingBorderS,
        },
        '&.Mui-focused': {
            backgroundColor: tokens.ColorSurfaceDefault,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.ColorBorderFocused,
            borderWidth: tokens.SizingBorderS,
        },
        '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.ColorBorderDanger,
            borderWidth: tokens.SizingBorderS,
        },
        '&.Mui-disabled': {
            backgroundColor: tokens.ColorSurfaceDisabled,
            color: tokens.ColorTextDisabled,
        },
        '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
            borderColor: `${tokens.ColorBorderDisabled}!important`,
            borderWidth: tokens.SizingBorderS,
        },
        '&.Mui-disabled .MuiInputAdornment-root': {
            color: tokens.ColorTextDisabled,
        },
    },
    '&:has(.MuiInputLabel-shrink) .MuiOutlinedInput-notchedOutline legend': {
        fontSize: tokens.TextXs.fontSize,
        maxWidth: `calc(100% - ${tokens.SizingSpacing100} - ${tokens.SizingBorderS})`,
    },
    '& .MuiOutlinedInput-input': {
        ...tokens.TextS,
        boxSizing: 'border-box',
        color: tokens.ColorTextBold,
        height: tokens.FontLineheightSm,
        minWidth: 0,
        padding: 0,
        textAlign: $textAlign,
        '&::placeholder': {
            color: tokens.ColorTextSubtle,
            opacity: 1,
        },
        '&.Mui-disabled': {
            color: tokens.ColorTextDisabled,
            WebkitTextFillColor: tokens.ColorTextDisabled,
        },
    },
    '& .Mui-error .MuiOutlinedInput-input::placeholder': {
        color: tokens.ColorTextDangerSubtle,
    },
    '& .MuiInputAdornment-root': {
        ...tokens.TextS,
        color: tokens.ColorTextSubtle,
        height: 'auto',
        marginLeft: 0,
        maxHeight: 'none',
        whiteSpace: 'nowrap',
    },
    '& .MuiFormHelperText-root': {
        ...tokens.TextXs,
        boxSizing: 'border-box',
        color: tokens.ColorTextDangerSubtle,
        height: tokens.FontLineheightXs,
        margin: `${tokens.SizingSpacing050} 0 0`,
        overflow: 'hidden',
        padding: `0 ${tokens.SizingSpacing150}`,
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        width: '100%',
    },
}))
